/**
 * Data-table cell utilities
 *
 * Pure, render-agnostic helpers shared by the data-table block renderers
 * (data_table_client + selectable_table): cell type guards, sort comparators,
 * primitive unwrapping for payloads, and the canonical cell renderer for the
 * `{ value, color }` / metric-comparison cell shapes.
 */

import React from 'react';
import type { SortingFn } from '@tanstack/react-table';
import type {
  ColoredCell,
  MetricComparisonCell,
  DataTableClientColumn,
  DataTableClientRow,
  DataTableClientSortType,
} from '@/types/ui-blocks';

/** Type guard: checks if a cell value is a MetricComparisonCell. */
export function isMetricComparisonCell(val: unknown): val is MetricComparisonCell {
  return typeof val === 'object' && val !== null
    && 'value' in val && 'change' in val && 'change_percentage' in val;
}

/** Type guard: checks if a cell value is a `{ value, color? }` object. */
export function isColoredCell(val: unknown): val is ColoredCell {
  return typeof val === 'object' && val !== null && 'value' in val && !('page_group_id' in val) && !('change_percentage' in val);
}

/** Extracts the sortable string from a raw cell value (unwraps ColoredCell/MetricComparisonCell). */
export function extractSortValue(raw: unknown): string {
  if (isMetricComparisonCell(raw)) return String(raw.value ?? '');
  if (isColoredCell(raw)) return String(raw.value ?? '');
  return String(raw ?? '');
}

/** Strips non-numeric characters (except `.` and `-`) and parses to float. */
export function parseNumeric(s: string): number {
  return parseFloat(s.replace(/[^0-9.-]/g, '')) || 0;
}

/** Returns a TanStack sortingFn appropriate for the given sortType. */
export function makeSortingFn(sortType: DataTableClientSortType): SortingFn<DataTableClientRow> {
  return (rowA, rowB, columnId) => {
    const a = extractSortValue(rowA.getValue(columnId));
    const b = extractSortValue(rowB.getValue(columnId));
    if (sortType === 'currency' || sortType === 'percentage') {
      return parseNumeric(a) - parseNumeric(b);
    }
    return a.localeCompare(b, undefined, { sensitivity: 'base' });
  };
}

/** Unwraps a cell value to a primitive — for selection payloads / serialization. */
export function unwrapCellValue(raw: DataTableClientRow[string]): string | number | null {
  if (raw === null || raw === undefined) return null;
  if (isMetricComparisonCell(raw)) return raw.value ?? null;
  if (isColoredCell(raw)) return raw.value ?? null;
  // PageRef or any other object value has no place in a primitive payload.
  if (typeof raw === 'object') return null;
  return raw;
}

/**
 * Renders a data-table cell value to a node: MetricComparisonCell as
 * "value vs change (change%)", ColoredCell as colored text, primitives as-is,
 * null/undefined as an em-dash. (Page navigation is handled by the caller.)
 */
export function renderDataTableCellContent(raw: DataTableClientRow[string]): React.ReactNode {
  if (isMetricComparisonCell(raw)) {
    const val = raw.value ?? '—';
    if (raw.change === null) return String(val);
    return (
      <span className="whitespace-nowrap">
        {String(val)} vs {String(raw.change)}
        {raw.change_percentage && (
          <span style={{ color: raw.color }}>{` (${raw.change_percentage})`}</span>
        )}
      </span>
    );
  }

  const value = isColoredCell(raw) ? raw.value : raw;
  const color = isColoredCell(raw) ? raw.color : undefined;
  // A non-null object that's neither a ColoredCell nor a MetricComparisonCell
  // (e.g. a PageRef) has no primitive rendering — show an em-dash rather than
  // leaking "[object Object]". Mirrors unwrapCellValue's object guard.
  if (value !== null && typeof value === 'object') return '—';
  const display = value === null || value === undefined ? '—' : String(value);
  return color ? <span style={{ color }}>{display}</span> : display;
}

// ---------------------------------------------------------------------------
// Wire-shape adaptation
//
// The chat path delivers table content in the "wire" shape — `columns: [{name,
// label, ...}]` and `rows: [{cells: [{value, color}]}]` (positional cells
// aligned with columns by index, the canonical workflow-apps schema). Report /
// demo consumers pass the legacy shape (`columns: [{key, label}]` + dynamic-key
// object rows). `adaptWireTableContent` normalises wire → legacy so renderers
// only deal with one shape; legacy content is returned unchanged.
// ---------------------------------------------------------------------------

interface WireColumn {
  name: string;
  label: string;
  enableSorting?: boolean | null;
  sortType?: DataTableClientSortType | null;
}
interface WireCell { value: string | number | null; color: string | null }
interface WireRow { cells: WireCell[] }

function isWireRow(row: unknown): row is WireRow {
  return typeof row === 'object'
    && row !== null
    && 'cells' in row
    && Array.isArray((row as { cells: unknown }).cells);
}

function isWireColumn(col: unknown): col is WireColumn {
  return typeof col === 'object' && col !== null && 'name' in col;
}

/**
 * Normalise table content from the chat wire shape to the legacy
 * `{key,label}` columns + object-row shape the renderers consume. Preserves any
 * sibling fields (title, idKey, …). Content already in legacy shape is returned
 * unchanged. Generic over the content type so both `data_table_client` and
 * `selectable_table` can use it.
 */
export function adaptWireTableContent<T extends { columns: unknown[]; rows: unknown[] }>(content: T): T {
  const wireCols = content.columns as unknown as WireColumn[];
  const wireRows = content.rows as unknown[];

  // Sampling col 0 OR row 0 suffices — a block's columns/rows are uniformly one
  // shape. The column check also saves an empty-rows wire table from rendering
  // broken headers (`columns[0].name` vs `columns[0].key`).
  const isWire = (wireRows.length > 0 && isWireRow(wireRows[0]))
    || (wireCols.length > 0 && isWireColumn(wireCols[0]));
  if (!isWire) return content;

  const columns: DataTableClientColumn[] = wireCols.map((col) => ({
    key: col.name,
    label: col.label,
    ...(col.enableSorting != null ? { enableSorting: col.enableSorting } : {}),
    ...(col.sortType != null ? { sortType: col.sortType } : {})
  }));

  const rows: DataTableClientRow[] = wireRows.map((row) => {
    const wire = row as WireRow;
    const out: DataTableClientRow = {};
    wireCols.forEach((col, i) => {
      const cell = wire.cells[i];
      if (cell == null) out[col.name] = null;
      else if (cell.color != null) out[col.name] = { value: cell.value, color: cell.color };
      else out[col.name] = cell.value;
    });
    return out;
  });

  return { ...content, columns, rows } as T;
}
